import React, { useState, useEffect, useMemo, useCallback } from "react";
import Swal from "sweetalert2"; 

export default function DocumentosLegales({ usuario, onBackToMenu }) {
  const [documentos, setDocumentos] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroSede, setFiltroSede] = useState("");
  
  const [mensaje, setMensaje] = useState({ text: "", type: "" });

  const [modalAbierto, setModalAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [docAEditar, setDocAEditar] = useState(null);
  
  // Bloqueo el botón para evitar doble/triple clic
  const [guardando, setGuardando] = useState(false); 

  const codigoBodegaUsuario = usuario?.codigo_bodega || usuario?.sede_id || "";

  const [nuevoDoc, setNuevoDoc] = useState({
    titulo: "", 
    sede_id: codigoBodegaUsuario, 
    fecha_vencimiento: "", 
    archivo_pdf: ""
  });

  useEffect(() => {
    if (mensaje.text) {
      const timer = setTimeout(() => setMensaje({ text: "", type: "" }), 4000);
      return () => clearTimeout(timer);
    }
  }, [mensaje]);

  const mostrarAlerta = (text, type = "error") => setMensaje({ text, type });

  // LIMPIEZA DE FECHA PARA LA TARJETA VISUAL
  const formatearFecha = (fechaCruda) => {
    if (!fechaCruda) return "N/A";
    try {
      return fechaCruda.split('T')[0]; // Corta el T05:00:00.000Z
    } catch(e) {
      return fechaCruda;
    }
  };

  // 1. GESTIÓN ABAC DE PERMISOS ESTRICTOS
  const permisosABAC = useMemo(() => {
    try {
      const parsed = typeof usuario?.permisos_avanzados === 'string' 
        ? JSON.parse(usuario.permisos_avanzados) 
        : (usuario?.permisos_avanzados || {});
      return parsed.micros || parsed;
    } catch (e) { 
      return {}; 
    }
  }, [usuario]);

  const esAdmin = String(usuario?.cargo || "").toUpperCase().includes("SUPER");
  const tieneVer = permisosABAC["Ver Documentos"] !== undefined || permisosABAC["documentos_legales"] !== undefined;
  const tieneCrear = permisosABAC["Crear Documentos"] !== undefined;
  const tieneActualizar = permisosABAC["Actualizar Documentos"] !== undefined;

  const permisoModulo = esAdmin || tieneVer || tieneCrear || tieneActualizar;
  const puedeCrear = esAdmin || tieneCrear;
  const puedeActualizar = esAdmin || tieneActualizar;

  const alcanceABAC = permisosABAC["Ver Documentos"]?.alcance || (esAdmin ? "GLOBAL" : "PROPIA");
  
  const sedesPermitidas = (permisosABAC["Ver Documentos"]?.sedes_ids || []).map(String);

  // 2. CARGAMOS LAS SEDES
  const cargarSedes = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/sedes`);
      const data = await res.json();
      if (data.success) {
        setSedes(data.sedes || data.data || []);
      }
    } catch (error) { 
      console.error("Error al cargar sedes:", error); 
    }
  }, []);

  // 3. CARGAMOS LOS DOCUMENTOS
  const cargarDocumentos = useCallback(async () => {
    setCargando(true);
    try {
      const sedeConsulta = alcanceABAC === 'GLOBAL' ? "" : codigoBodegaUsuario;
      const urlDocs = `${process.env.REACT_APP_API_URL}/api/documentos-legales?sede_id=${sedeConsulta}&alcance=${alcanceABAC}`;
      
      const headers = { "Content-Type": "application/json" };
      if (usuario) {
        headers["x-user-data"] = JSON.stringify(usuario);
        headers["x-abac-permissions"] = JSON.stringify(permisosABAC);
      }

      const res = await fetch(urlDocs, { headers });
      const data = await res.json();
      
      if (data.success) {
        setDocumentos(data.data || data.documentos || []);
      }
    } catch (error) { 
      mostrarAlerta("Error al cargar la lista de documentos", "error"); 
    } finally { 
      setCargando(false); 
    }
  }, [alcanceABAC, codigoBodegaUsuario, usuario, permisosABAC]);

  useEffect(() => {
    if (permisoModulo) { 
      cargarSedes(); 
      cargarDocumentos(); 
    }
  }, [permisoModulo, cargarSedes, cargarDocumentos]);

  const documentosFiltrados = useMemo(() => {
    if (!filtroSede) return documentos; 
    return documentos.filter(doc => String(doc.sede_id) === String(filtroSede));
  }, [documentos, filtroSede]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") { 
        mostrarAlerta("Por favor seleccione un archivo formato PDF válido.", "error"); 
        return; 
      }
      const reader = new FileReader();
      reader.onloadend = () => setNuevoDoc(prev => ({ ...prev, archivo_pdf: reader.result }));
      reader.readAsDataURL(file);
    }
  };

  const abrirModalCrear = () => {
    setModoEdicion(false); 
    setDocAEditar(null);
    setNuevoDoc({ 
      titulo: "", 
      sede_id: codigoBodegaUsuario, 
      fecha_vencimiento: "", 
      archivo_pdf: "" 
    });
    setModalAbierto(true);
  };

  const abrirModalActualizar = (doc) => {
    setModoEdicion(true); 
    setDocAEditar(doc);
    setNuevoDoc({ 
      titulo: doc.titulo, 
      sede_id: doc.sede_id, 
      fecha_vencimiento: formatearFecha(doc.fecha_vencimiento), 
      archivo_pdf: "" 
    });
    setModalAbierto(true);
  };

  const abrirPDF = (base64String) => {
    if (!base64String) return mostrarAlerta("El documento no tiene un archivo adjunto.", "error");
    if (base64String.startsWith('http')) return window.open(base64String, '_blank');
    
    try {
      const base64Parts = base64String.split(','); 
      const fileData = base64Parts.length > 1 ? base64Parts[1] : base64Parts[0];
      const byteCharacters = atob(fileData); 
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers); 
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(blob); 
      window.open(fileURL, '_blank');
    } catch (error) { 
      mostrarAlerta("El archivo está corrupto o es demasiado pesado.", "error"); 
    }
  };

  const handleEliminar = async () => {
    const confirmacion = await Swal.fire({
      title: '¿Eliminar Documento?',
      html: `Estás a punto de borrar <b>"${docAEditar.titulo}"</b>.<br/>Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: '🗑️ Sí, eliminar',
      cancelButtonText: 'Cancelar',
      customClass: {
        popup: 'rounded-3xl shadow-2xl',
        title: 'text-xl font-black text-slate-800',
        htmlContainer: 'text-sm text-slate-600 font-medium'
      }
    });

    if (!confirmacion.isConfirmed) return;
    
    setGuardando(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/documentos-legales/${docAEditar.id}`, { 
        method: "DELETE" 
      });
      const data = await res.json();
      
      if (data.success) { 
        Swal.fire({
          title: '¡Eliminado!',
          text: 'El documento ha sido borrado con éxito.',
          icon: 'success',
          confirmButtonColor: '#10b981',
          customClass: { popup: 'rounded-3xl' }
        });
        setModalAbierto(false); 
        cargarDocumentos(); 
      } else {
        mostrarAlerta(data.message || "Error al eliminar", "error");
      }
    } catch (error) { 
      mostrarAlerta("Error de red al intentar eliminar.", "error"); 
    } finally {
      setGuardando(false);
    }
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true); // Bloquea el botón
    
    if (modoEdicion) {
      if (!puedeActualizar) {
        setGuardando(false);
        return mostrarAlerta("No tienes permisos para actualizar.", "error");
      }
      try {
        const payload = { 
          nueva_fecha: nuevoDoc.fecha_vencimiento, 
          nuevo_pdf: nuevoDoc.archivo_pdf || null 
        };
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/documentos-legales/${docAEditar.id}`, { 
          method: "PUT", 
          headers: { "Content-Type": "application/json" }, 
          body: JSON.stringify(payload) 
        });
        const data = await res.json();
        if (data.success) { 
          mostrarAlerta("¡Actualizado!", "success"); 
          setModalAbierto(false); 
          cargarDocumentos(); 
        } else {
          mostrarAlerta(data.message || "Error al actualizar", "error");
        }
      } catch (error) { 
        mostrarAlerta("Error de red", "error"); 
      } finally {
        setGuardando(false); // Libera el botón
      }
    } else {
      if (!puedeCrear) {
        setGuardando(false);
        return mostrarAlerta("No tienes permisos para crear.", "error");
      }
      if (!nuevoDoc.archivo_pdf) {
        setGuardando(false);
        return mostrarAlerta("Adjunte un PDF.", "error");
      }
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/documentos-legales`, { 
          method: "POST", 
          headers: { "Content-Type": "application/json" }, 
          body: JSON.stringify(nuevoDoc) 
        });
        const data = await res.json();
        if (data.success) { 
          mostrarAlerta("¡Guardado exitosamente!", "success"); 
          setModalAbierto(false); 
          cargarDocumentos(); 
        } else {
          mostrarAlerta(data.message || "Error al guardar", "error");
        }
      } catch (error) { 
        mostrarAlerta("Error de red", "error"); 
      } finally {
        setGuardando(false); // Libera el botón
      }
    }
  };

  if (!permisoModulo) {
    return (
      <div className="w-full min-h-screen py-8 px-4 flex flex-col items-center animate-fade-in relative z-10">
        <div className="bg-white/50 backdrop-blur-[45px] rounded-[34px] shadow-[0_25px_70px_rgba(0,0,0,0.15)] p-10 max-w-4xl w-full mx-auto border border-white/80 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-red-500 to-orange-500"></div>
          <span className="text-6xl block mb-6">🔒</span>
          <h2 className="text-2xl font-black text-gray-900 mb-2">Acceso Denegado</h2>
          <p className="text-sm text-gray-600 mb-8 font-medium">No cuentas con ningún privilegio (Ver, Crear o Actualizar) asignado en la matriz ABAC para Documentos Legales.</p>
          <button onClick={onBackToMenu} className="px-6 py-2.5 bg-[#007aff] hover:bg-[#0062cc] text-white font-bold rounded-full transition shadow-md cursor-pointer">
            Volver al Menú Principal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen py-6 px-4 flex flex-col items-center animate-fade-in relative z-10">
      <div className="relative bg-white/50 backdrop-blur-[45px] rounded-[34px] shadow-[0_25px_70px_rgba(0,0,0,0.15)] p-6 sm:p-10 max-w-7xl w-full mx-auto h-[90vh] flex flex-col border border-white/80 overflow-hidden">
        
        {mensaje.text && (
          <div className={`absolute top-8 right-8 z-[99999] px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 transition-all duration-500 animate-fade-in ${ mensaje.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800' }`}>
            <span className="text-2xl">{mensaje.type === 'success' ? '✅' : '⚠️'}</span>
            <span className="font-bold text-sm tracking-tight pr-4">{mensaje.text}</span>
            <button onClick={() => setMensaje({ text: "", type: "" })} className="ml-2 font-black cursor-pointer opacity-70 hover:opacity-100">✕</button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-900/10 pb-6 mb-8 gap-4 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/70 border border-white/90 flex items-center justify-center text-2xl shadow-sm text-blue-600">📁</div>
            <div>
              <h2 className="text-2xl font-black text-gray-900 tracking-tight leading-tight">Documentos Legales</h2>
              <p className="text-xs sm:text-sm text-gray-600 font-bold mt-1">Gestión y control de vigencias normativas.</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto bg-white/40 p-1.5 rounded-xl border border-white/60">
            {(alcanceABAC === 'GLOBAL' || alcanceABAC === 'ESPECIFICA') ? (
              <select 
                value={filtroSede} 
                onChange={(e) => setFiltroSede(e.target.value)} 
                className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-xs font-bold text-gray-800 shadow-sm outline-none cursor-pointer focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{alcanceABAC === 'GLOBAL' ? "Todas las Sedes" : "Todas mis Sedes Asignadas"}</option>
                {Array.isArray(sedes) && sedes
                  .filter(s => alcanceABAC === 'GLOBAL' || sedesPermitidas.includes(String(s.codigo_bodega || s.id)))
                  .map(s => (
                  <option key={s.id || s.codigo_bodega} value={s.codigo_bodega || s.id}>
                    {s.nombre} {s.codigo_bodega ? `(Cód: ${s.codigo_bodega})` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <div className="px-4 py-2 rounded-lg bg-white/80 border border-white text-xs font-bold text-gray-700 shadow-sm">
                🏢 Mi Sede (Cód: {codigoBodegaUsuario || 'N/A'})
              </div>
            )}

            {puedeCrear && (
              <button 
                onClick={abrirModalCrear} 
                className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold shadow-md transition cursor-pointer"
              >
                + Nuevo
              </button>
            )}

            <button onClick={onBackToMenu} className="px-4 py-2 rounded-lg text-sm font-bold text-red-500 hover:bg-red-50 hover:text-red-700 transition-all cursor-pointer">
              ✕ Cerrar
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          {cargando ? (
            <div className="text-center py-20 font-bold text-gray-500 animate-pulse">Cargando documentos legales...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.isArray(documentosFiltrados) && documentosFiltrados.length > 0 ? (
                documentosFiltrados.map(doc => {
                  const esVencido = doc.estado === 'VENCIDO';
                  return (
                    <div key={doc.id} className="bg-white/40 backdrop-blur-xl border border-white/70 rounded-[28px] p-5 shadow-sm flex flex-col justify-between transition hover:shadow-md hover:bg-white/60">
                      <div>
                        <div className="flex justify-between items-center mb-4">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-black tracking-wider ${esVencido ? 'bg-red-500 text-white shadow-[0_0_10px_rgba(239,68,68,0.3)]' : 'bg-emerald-500 text-white shadow-[0_0_10px_rgba(16,185,129,0.3)]'}`}>
                            {doc.estado}
                          </span>
                          <span className="text-[10px] font-extrabold text-blue-600 bg-blue-100/50 px-2 py-0.5 rounded-md border border-blue-200/50 uppercase">
                            Sede: {doc.sede_id}
                          </span>
                        </div>
                        <h3 className="font-extrabold text-gray-900 text-sm mb-3 line-clamp-2" title={doc.titulo}>{doc.titulo}</h3>
                        <div className="bg-white/70 p-3 rounded-2xl mb-4 border border-white/50 text-xs font-bold text-gray-700 flex items-center justify-between shadow-inner">
                          <span>Vencimiento:</span>
                          <span className="font-mono font-bold text-gray-900">
                            {formatearFecha(doc.fecha_vencimiento)}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2 border-t border-gray-900/10">
                        <button 
                          onClick={() => abrirPDF(doc.archivo_pdf)} 
                          className="flex-1 py-2.5 bg-[#007aff] hover:bg-[#0062cc] text-white font-bold rounded-xl text-xs uppercase tracking-wide flex items-center justify-center gap-1.5 transition-all shadow-[0_4px_14px_rgba(0,122,255,0.3)] cursor-pointer"
                        >
                          📄 Abrir PDF
                        </button>
                        
                        {puedeActualizar && (
                          <button 
                            onClick={() => abrirModalActualizar(doc)} 
                            className="px-4 py-2.5 rounded-xl text-xs font-black transition border cursor-pointer bg-amber-500 hover:bg-amber-600 text-white border-transparent shadow-[0_4px_14px_rgba(245,158,11,0.3)] hover:-translate-y-0.5"
                          >
                            Editar
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full text-center py-16 bg-white/40 rounded-3xl border border-white/60">
                  <p className="text-gray-600 font-bold text-sm">No hay documentos legales registrados para la selección actual.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* MODAL CREAR/EDITAR */}
        {modalAbierto && (
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-md flex items-center justify-center z-[9000] p-4">
            <div className="bg-white/90 backdrop-blur-2xl rounded-[30px] p-8 max-w-md w-full shadow-2xl border border-white animate-fade-in-up">
              
              <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-3">
                <h3 className="text-xl font-black text-gray-900">
                  {modoEdicion ? "Actualizar Documento" : "Agregar Documento"}
                </h3>
                
                {/* BOTÓN ELIMINAR*/}
                {modoEdicion && puedeActualizar && (
                  <button 
                    type="button" 
                    onClick={handleEliminar}
                    disabled={guardando}
                    className="text-xs font-bold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors cursor-pointer flex items-center gap-1"
                  >
                    🗑️ Borrar
                  </button>
                )}
              </div>
              
              <form onSubmit={handleGuardar} className="space-y-5">
                <div>
                  <label className="block text-[10px] uppercase font-black mb-1.5 text-gray-600">Título del Documento *</label>
                  <input 
                    type="text" 
                    required 
                    disabled={modoEdicion || guardando} 
                    value={nuevoDoc.titulo} 
                    onChange={e => setNuevoDoc({...nuevoDoc, titulo: e.target.value})} 
                    className={`w-full px-4 py-3 border border-gray-200 rounded-2xl text-xs font-bold bg-white/70 outline-none shadow-inner ${modoEdicion || guardando ? 'opacity-60 cursor-not-allowed' : 'focus:border-blue-500 focus:ring-2 focus:ring-blue-100'}`} 
                    placeholder="Ej. Bomberos, Uso de Suelos..." 
                  />
                </div>
                
                <div>
                  <label className="block text-[10px] uppercase font-black mb-1.5 text-gray-600">Sede *</label>
                  <select 
                    required 
                    disabled={modoEdicion || guardando} 
                    value={nuevoDoc.sede_id} 
                    onChange={e => setNuevoDoc({...nuevoDoc, sede_id: e.target.value})} 
                    className={`w-full px-4 py-3 border border-gray-200 rounded-2xl text-xs font-bold bg-white/70 outline-none shadow-inner appearance-none ${modoEdicion || guardando ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer focus:border-blue-500 focus:ring-2 focus:ring-blue-100'}`}
                  >
                    <option value="">Seleccione Sede...</option>
                    {Array.isArray(sedes) && sedes.map(s => (
                      <option key={s.id || s.codigo_bodega} value={s.codigo_bodega || s.id}>
                        {s.nombre} {s.codigo_bodega ? `(Cód: ${s.codigo_bodega})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-[10px] uppercase font-black mb-1.5 text-blue-600">
                    {modoEdicion ? "Nueva Fecha Vencimiento *" : "Fecha Vencimiento *"}
                  </label>
                  <input 
                    type="date" 
                    required 
                    disabled={guardando}
                    value={nuevoDoc.fecha_vencimiento} 
                    onChange={e => setNuevoDoc({...nuevoDoc, fecha_vencimiento: e.target.value})} 
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl text-xs font-bold bg-white/70 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 shadow-inner" 
                  />
                </div>
                
                <div>
                  <label className="block text-[10px] uppercase font-black mb-1.5 text-gray-600">
                    {modoEdicion ? "Nuevo Archivo PDF (Opcional)" : "Archivo PDF *"}
                  </label>
                  <input 
                    type="file" 
                    accept="application/pdf" 
                    required={!modoEdicion}
                    disabled={guardando}
                    onChange={handleFileChange} 
                    className="w-full text-xs font-bold text-gray-600 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-black file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer shadow-inner" 
                  />
                  {modoEdicion && (
                    <p className="text-[10px] text-gray-500 mt-2 font-medium">Si no selecciona archivo, se conservará el anterior.</p>
                  )}
                </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button 
                    type="button" 
                    disabled={guardando}
                    onClick={() => setModalAbierto(false)} 
                    className="px-5 py-2.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-bold transition cursor-pointer disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit" 
                    disabled={guardando}
                    className="px-6 py-2.5 rounded-full bg-[#007aff] hover:bg-[#0062cc] text-white text-xs font-bold shadow-md transition cursor-pointer hover:-translate-y-0.5 disabled:opacity-50 disabled:bg-blue-400 disabled:cursor-wait"
                  >
                    {guardando ? "⏳ Guardando..." : modoEdicion ? "Actualizar" : "Guardar PDF"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}